import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { CircuitState } from '../models';

/**
 * Circuit breaker configuration
 */
interface CircuitBreakerConfig {
  readonly failureThreshold: number;
  readonly recoveryTimeoutMs: number;
  readonly halfOpenMaxAttempts: number;
}

/**
 * Circuit breaker service to prevent cascading failures
 */
@Injectable({
  providedIn: 'root'
})
export class CircuitBreakerService implements OnDestroy {
  private readonly defaultConfig: CircuitBreakerConfig = {
    failureThreshold: 4,        // Open after 4 consecutive failures (matching ping system)
    recoveryTimeoutMs: 30000,   // 30 seconds recovery timeout (matching emergency cooldown)
    halfOpenMaxAttempts: 2      // Max 2 attempts in half-open state
  };

  private config: CircuitBreakerConfig = { ...this.defaultConfig };
  private failureCount = 0;
  private lastFailureTime?: Date;
  private halfOpenAttempts = 0; // Track attempts in half-open state
  private state = new BehaviorSubject<CircuitState>({
    isOpen: false,
    failureCount: 0
  });

  public readonly circuitState$: Observable<CircuitState> = this.state.pipe(
    distinctUntilChanged((prev, curr) =>
      prev.isOpen === curr.isOpen && prev.failureCount === curr.failureCount
    )
  );

  /**
   * Check if circuit allows operation
   */
  public canExecute(): boolean {
    const currentState = this.state.value;

    if (!currentState.isOpen) {
      return true; // Circuit is closed, allow execution
    }

    // Circuit is open, check if recovery time has passed
    if (this.shouldAttemptRecovery()) {
      this.transitionToHalfOpen();
      return true;
    }

    return false;
  }

  /**
   * Record a successful operation
   */
  public recordSuccess(): void {
    const currentState = this.state.value;

    if (currentState.isOpen && this.shouldAttemptRecovery()) {
      // Success in half-open state - close the circuit
      console.log('✅ CIRCUIT BREAKER: Success in half-open state, closing circuit');
      this.resetFailureCount();
      this.closeCircuit();
      this.halfOpenAttempts = 0;
    } else if (!currentState.isOpen) {
      // Success in closed state - just reset failure count
      this.resetFailureCount();
    }
  }

  /**
   * Record a failed operation
   */
  public recordFailure(): void {
    this.failureCount++;
    this.lastFailureTime = new Date();
    const currentState = this.state.value;

    console.log('❌ CIRCUIT BREAKER: Recording failure', {
      failureCount: this.failureCount,
      threshold: this.config.failureThreshold,
      currentlyOpen: currentState.isOpen,
      timestamp: this.lastFailureTime.toISOString()
    });

    if (currentState.isOpen && this.shouldAttemptRecovery()) {
      // Failure in half-open state - reopen circuit immediately
      this.halfOpenAttempts = 0;
      console.warn('⚠️ CIRCUIT BREAKER: Failure in half-open state, reopening circuit');
      this.openCircuit();
    } else if (this.failureCount >= this.config.failureThreshold) {
      this.openCircuit();
    } else {
      this.updateState();
    }
  }

  /**
   * Get current circuit state
   */
  public getState(): CircuitState {
    return this.state.value;
  }

  /**
   * Check if circuit is open
   */
  public isOpen(): boolean {
    return this.state.value.isOpen;
  }

  /**
   * Get time until next retry attempt
   */
  public getTimeUntilRetry(): number {
    if (!this.isOpen() || !this.lastFailureTime) {
      return 0;
    }

    const timeSinceFailure = Date.now() - this.lastFailureTime.getTime();
    const timeUntilRetry = this.config.recoveryTimeoutMs - timeSinceFailure;

    return Math.max(0, timeUntilRetry);
  }

  /**
   * Manually reset the circuit breaker
   */
  public reset(): void {
    this.resetFailureCount();
    this.closeCircuit();
  }

  /**
   * Update circuit breaker configuration
   */
  public updateConfig(newConfig: Partial<CircuitBreakerConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * Get current configuration
   */
  public getConfig(): CircuitBreakerConfig {
    return { ...this.config };
  }

  // Private methods

  private shouldAttemptRecovery(): boolean {
    if (!this.lastFailureTime) {
      return true;
    }

    const timeSinceFailure = Date.now() - this.lastFailureTime.getTime();
    const canAttemptRecovery = timeSinceFailure >= this.config.recoveryTimeoutMs;

    if (canAttemptRecovery) {
      console.log('🔄 CIRCUIT BREAKER: Recovery timeout elapsed, can attempt recovery', {
        timeSinceFailure,
        recoveryTimeout: this.config.recoveryTimeoutMs
      });
    }

    return canAttemptRecovery;
  }

  private transitionToHalfOpen(): void {
    // In half-open state, we allow limited attempts
    this.halfOpenAttempts = 0;
    console.log('🔄 CIRCUIT BREAKER: Transitioning to half-open state');
    this.closeCircuit(); // Circuit appears closed but we're monitoring closely
  }

  private openCircuit(): void {
    const nextRetryTime = this.lastFailureTime
      ? new Date(this.lastFailureTime.getTime() + this.config.recoveryTimeoutMs)
      : undefined;

    console.error('🚫 CIRCUIT BREAKER: Opening circuit', {
      failureCount: this.failureCount,
      threshold: this.config.failureThreshold,
      nextRetryTime: nextRetryTime?.toISOString(),
      recoveryTimeoutMs: this.config.recoveryTimeoutMs
    });

    this.state.next({
      isOpen: true,
      failureCount: this.failureCount,
      lastFailureTime: this.lastFailureTime,
      nextRetryTime
    });
  }

  private closeCircuit(): void {
    console.log('✅ CIRCUIT BREAKER: Closing circuit', {
      previousFailureCount: this.failureCount,
      wasOpen: this.state.value.isOpen
    });

    this.state.next({
      isOpen: false,
      failureCount: this.failureCount,
      lastFailureTime: this.lastFailureTime
    });
  }

  private resetFailureCount(): void {
    this.failureCount = 0;
    this.lastFailureTime = undefined;
  }

  private updateState(): void {
    this.state.next({
      isOpen: false,
      failureCount: this.failureCount,
      lastFailureTime: this.lastFailureTime
    });
  }

  /**
   * Get failure statistics
   */
  public getStats(): {
    failureCount: number;
    isOpen: boolean;
    timeUntilRetry: number;
    lastFailureTime?: Date;
    config: CircuitBreakerConfig;
    halfOpenAttempts: number;
  } {
    return {
      failureCount: this.failureCount,
      isOpen: this.isOpen(),
      timeUntilRetry: this.getTimeUntilRetry(),
      lastFailureTime: this.lastFailureTime,
      config: this.getConfig(),
      halfOpenAttempts: this.halfOpenAttempts
    };
  }

  /**
   * Cleanup resources to prevent memory leaks
   */
  ngOnDestroy(): void {
    // Complete the state subject
    this.state.complete();
  }
}
